import { useEffect, useRef, useState } from 'react';
import { archive, type ArchiveDocumentListItem } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { validateUploadSize, MAX_UPLOAD_BYTES, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

/**
 * Karta wyświetlająca pliki archiwum powiązane z ujęciem albo klientem.
 * Upload działa dwuetapowo:
 *   1) user wybiera pliki → trafiają do staging-listy z per-plik checkboxem "RAG"
 *   2) user może wyłączyć indeksację dla wybranych plików (np. NDA, faktury)
 *   3) klika "Wgraj N plików" → upload leci po kolei z indywidualną flagą per plik
 */
interface Props {
  scope: 'intake' | 'client' | 'borehole' | 'employee' | 'vehicle' | 'invoice';
  scopeId: number;
  scopeName?: string | null;
}

interface StagedFile {
  file: File;
  ragEnabled: boolean;
}

const PREVIEWABLE = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
const extOf = (name: string) => (name.split('.').pop() || '').toLowerCase();
export const isPreviewable = (name: string) => PREVIEWABLE.includes(extOf(name));

export function ArchiveFilesCard({ scope, scopeId, scopeName }: Props) {
  const [files, setFiles] = useState<ArchiveDocumentListItem[] | null>(null);
  const [preview, setPreview] = useState<ArchiveDocumentListItem | null>(null);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);   // null = brak wysyłki (bieżący plik)
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  /** Grace period (z konfiguracji systemu). Domyślnie 7, ale admin może zmienić. */
  const [graceDays, setGraceDays] = useState<number>(7);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const reload = () => {
    const p = scope === 'intake' ? archive.filesForIntake(scopeId)
      : scope === 'borehole' ? archive.filesForBorehole(scopeId)
      : scope === 'employee' ? archive.filesForEmployee(scopeId)
      : scope === 'vehicle' ? archive.filesForVehicle(scopeId)
      : scope === 'invoice' ? archive.filesForInvoice(scopeId)
      : archive.filesForClient(scopeId);
    p.then(setFiles).catch(e => setErr(String(e)));
  };

  useEffect(() => {
    reload();
    archive.softDeleteConfig()
      .then(c => setGraceDays(c.graceDays))
      .catch(() => { /* zostaje default 7 */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, scopeId]);

  /** User wybrał pliki w pickerze — dorzuć do staging-listy (każdy z domyślnym RAG=true). */
  function onPickFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const arr = Array.from(list).filter(f => f.size > 0);
    if (arr.length === 0) return;
    // Twardy limit 100 MB/plik — odrzuć zbyt duże, ale pozwól dorzucić resztę.
    const sizeErr = validateUploadSize(arr);
    const okArr = sizeErr ? arr.filter(f => f.size <= MAX_UPLOAD_BYTES) : arr;
    if (sizeErr) setErr(sizeErr);
    if (okArr.length === 0) { if (inputRef.current) inputRef.current.value = ''; return; }
    // Załączniki referencyjne (otwór, pracownik, pojazd) domyślnie bez indeksacji RAG —
    // RAG włączamy tylko dla dokumentów ujęć/klientów, które AI wykorzystuje do draftów.
    const defaultRag = scope === 'intake' || scope === 'client';
    setStaged(prev => [...prev, ...okArr.map(f => ({ file: f, ragEnabled: defaultRag }))]);
    if (inputRef.current) inputRef.current.value = '';
  }

  function setStagedRag(idx: number, ragEnabled: boolean) {
    setStaged(prev => prev.map((s, i) => i === idx ? { ...s, ragEnabled } : s));
  }

  function setAllStagedRag(ragEnabled: boolean) {
    setStaged(prev => prev.map(s => ({ ...s, ragEnabled })));
  }

  function removeStaged(idx: number) {
    setStaged(prev => prev.filter((_, i) => i !== idx));
  }

  function clearStaged() {
    setStaged([]);
  }

  /** Wykonuje upload wszystkich staged plików z ich indywidualnymi flagami ragEnabled. */
  async function uploadAllStaged() {
    if (staged.length === 0) return;
    setErr(null); setInfo(null);
    let uploaded = 0;
    let dups = 0;
    let ragOn = 0;
    let ragOff = 0;
    for (let i = 0; i < staged.length; i++) {
      setUploadingIdx(i);
      setUploadPct(0);   // start paska postępu dla bieżącego pliku
      const item = staged[i];
      const onProgress = (p: UploadProgress) => setUploadPct(p.percent);
      try {
        const r = scope === 'intake'
          ? await archive.uploadForIntake(scopeId, item.file, { ragEnabled: item.ragEnabled }, onProgress)
          : scope === 'borehole'
          ? await archive.uploadForBorehole(scopeId, item.file, { ragEnabled: item.ragEnabled }, onProgress)
          : scope === 'employee'
          ? await archive.uploadForEmployee(scopeId, item.file, { ragEnabled: item.ragEnabled }, onProgress)
          : scope === 'vehicle'
          ? await archive.uploadForVehicle(scopeId, item.file, { ragEnabled: item.ragEnabled }, onProgress)
          : scope === 'invoice'
          ? await archive.uploadForInvoice(scopeId, item.file, { ragEnabled: item.ragEnabled }, onProgress)
          : await archive.uploadForClient(scopeId, item.file, { ragEnabled: item.ragEnabled }, onProgress);
        if (r.duplicate) dups++; else uploaded++;
        if (item.ragEnabled) ragOn++; else ragOff++;
      } catch (e) {
        setErr(`${item.file.name}: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setUploadPct(null);   // reset paska między plikami (i po ostatnim)
      }
    }
    setUploadingIdx(null);
    setStaged([]);
    reload();
    const parts = [];
    if (uploaded > 0) parts.push(`Wgrano ${uploaded}`);
    if (dups > 0) parts.push(`pominięto ${dups} duplikat${dups === 1 ? '' : 'y'}`);
    if (ragOn > 0 || ragOff > 0) parts.push(`RAG: ${ragOn} aktywne / ${ragOff} pominięte`);
    if (parts.length > 0) setInfo(parts.join(' · ') + '.');
  }

  async function onToggleRag(item: ArchiveDocumentListItem) {
    const newVal = !item.ragEnabled;
    if (!newVal && !confirm(`Wyłączyć indeksację RAG dla "${item.name}"? Plik zostanie w archiwum, ale AI go nie znajdzie.`)) return;
    setTogglingId(item.id);
    setErr(null); setInfo(null);
    try {
      await archive.setRagEnabled(item.id, newVal);
      setInfo(newVal
        ? `Włączono indeksację RAG dla "${item.name}". ${item.fragmentsCount === 0 ? 'Reindex w toku — może potrwać kilkanaście sekund.' : ''}`
        : `Wyłączono indeksację RAG dla "${item.name}".`);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTogglingId(null);
    }
  }

  async function onRequestDelete(item: ArchiveDocumentListItem) {
    const dni = graceDays === 1 ? 'dzień' : graceDays < 5 ? 'dni' : 'dni';
    if (!confirm(`Oznaczyć "${item.name}" do usunięcia?\nPlik zostanie skasowany trwale za ${graceDays} ${dni}; do tego czasu możesz cofnąć usunięcie.`)) return;
    setTogglingId(item.id);
    setErr(null); setInfo(null);
    try {
      const res = await archive.requestDelete(item.id);  // backend bierze domyślny z konfiguracji
      if (res.alreadyPending) {
        setInfo(`"${item.name}" był już oznaczony do usunięcia. Termin: ${res.deleteAfter}.`);
      } else {
        setInfo(`Oznaczono "${item.name}" do usunięcia. Trwałe skasowanie: ${res.deleteAfter}.`);
      }
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTogglingId(null);
    }
  }

  async function onCancelDelete(item: ArchiveDocumentListItem) {
    setTogglingId(item.id);
    setErr(null); setInfo(null);
    try {
      await archive.cancelDelete(item.id);
      setInfo(`Cofnięto usunięcie "${item.name}" — plik znowu aktywny.`);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTogglingId(null);
    }
  }

  const titleSuffix = scope === 'intake' ? 'ujęcia'
    : scope === 'borehole' ? 'otworu'
    : scope === 'employee' ? 'pracownika'
    : scope === 'vehicle' ? 'pojazdu'
    : scope === 'invoice' ? 'faktury'
    : 'klienta';
  const isUploading = uploadingIdx !== null;
  const stagedRagCount = staged.filter(s => s.ragEnabled).length;
  // Faktury są bramkowane FINANCE_VIEW i z założenia NIE trafiają do korpusów asystenta
  // (decyzja 2 z V128) — backend wymusza ragEnabled=false, więc checkbox nie może udawać wyboru.
  const ragLocked = scope === 'invoice';

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Pliki {titleSuffix}{scopeName ? `: ${scopeName}` : ''}</div>
        <span className="ml-2 text-[11px] text-ink-400">
          {files ? `${files.length} plików` : '…'}
        </span>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="ml-auto btn btn-secondary btn-sm"
        >
          <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj pliki
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => onPickFiles(e.target.files)}
        />
      </div>
      <div className="card-body !p-0">
        {err && <div className="p-3 text-red-700 bg-red-50 border-b border-red-200 text-[12px]">Błąd: {err}</div>}
        {info && <div className="p-3 text-emerald-700 bg-emerald-50 border-b border-emerald-200 text-[12px]">{info}</div>}

        {/* Staging area — pojawia się po wybraniu plików w pickerze */}
        {staged.length > 0 && (
          <div className="border-b border-ink-100 bg-amber-50/30">
            <div className="px-4 py-2 flex items-center gap-2 border-b border-ink-100">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-600">
                Do wgrania: {staged.length} plik{staged.length === 1 ? '' : staged.length < 5 ? 'i' : 'ów'}
              </span>
              <span className="text-[11px] text-ink-500">
                {ragLocked
                  ? '· indeksacja AI wyłączona (dokument finansowy)'
                  : `· RAG aktywny dla ${stagedRagCount}/${staged.length}`}
              </span>
              <button
                onClick={() => setAllStagedRag(true)}
                hidden={ragLocked}
                disabled={isUploading || stagedRagCount === staged.length}
                className="ml-2 text-[11px] text-water-700 hover:underline disabled:opacity-40 disabled:no-underline"
                title="Włącz indeksację RAG dla wszystkich"
              >wszystkie ✓</button>
              <button
                onClick={() => setAllStagedRag(false)}
                hidden={ragLocked}
                disabled={isUploading || stagedRagCount === 0}
                className="text-[11px] text-water-700 hover:underline disabled:opacity-40 disabled:no-underline"
                title="Wyłącz indeksację RAG dla wszystkich"
              >wszystkie ✗</button>
              <div className="ml-auto flex gap-2">
                <button
                  onClick={clearStaged}
                  disabled={isUploading}
                  className="btn btn-secondary btn-sm"
                >Anuluj</button>
                <button
                  onClick={uploadAllStaged}
                  disabled={isUploading}
                  className="btn btn-primary btn-sm"
                >
                  {isUploading
                    ? <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                        <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg> Wgrywam {(uploadingIdx ?? 0) + 1}/{staged.length}…</>
                    : <><Icon name="check" className="w-3.5 h-3.5"/> Wgraj {staged.length} plik{staged.length === 1 ? '' : staged.length < 5 ? 'i' : 'ów'}</>}
                </button>
              </div>
            </div>
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-8">RAG</th>
                  <th>Nazwa pliku</th>
                  <th className="text-right">Rozmiar</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {staged.map((item, idx) => {
                  const isCurrent = uploadingIdx === idx;
                  const isPast = uploadingIdx !== null && idx < uploadingIdx;
                  return (
                    <tr key={`${item.file.name}-${idx}`}
                        className={isCurrent ? 'bg-water-50' : isPast ? 'opacity-50' : ''}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={ragLocked ? false : item.ragEnabled}
                          onChange={e => setStagedRag(idx, e.target.checked)}
                          disabled={isUploading || ragLocked}
                          className={'w-4 h-4 ' + (ragLocked ? 'cursor-not-allowed' : 'cursor-pointer')}
                          title={ragLocked
                            ? 'Faktury nie są indeksowane dla AI — dokumenty finansowe są wyłączone z korpusów asystenta.'
                            : item.ragEnabled
                            ? 'Plik będzie zindeksowany dla AI-draftów. Odznacz aby pominąć.'
                            : 'Plik NIE będzie indeksowany dla AI. Zaznacz aby włączyć indeksację RAG.'}
                        />
                      </td>
                      <td>
                        <div className="font-semibold text-ink-900 text-[13px] max-w-[420px] truncate"
                             title={item.file.name}>{item.file.name}</div>
                        {isCurrent && uploadPct !== null && (
                          <div className="mt-1 max-w-[420px]">
                            <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" compact />
                          </div>
                        )}
                        {!item.ragEnabled && (
                          <div className="text-[10px] text-amber-700">⚠ pominięty przez AI (nie zostanie zaindeksowany)</div>
                        )}
                      </td>
                      <td className="text-right font-mono tabular-nums text-[12px]">
                        {item.file.size < 1024 ? `${item.file.size} B`
                        : item.file.size < 1024 * 1024 ? `${(item.file.size / 1024).toFixed(1)} KB`
                        : `${(item.file.size / 1024 / 1024).toFixed(1)} MB`}
                      </td>
                      <td className="text-right">
                        <button
                          onClick={() => removeStaged(idx)}
                          disabled={isUploading}
                          className="text-ink-400 hover:text-red-600 p-1"
                          title="Usuń z listy do wgrania"
                        >
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!files && <div className="p-4 text-ink-400 text-[12px]">Ładowanie…</div>}
        {files && files.length === 0 && staged.length === 0 && (
          <div className="p-6 text-center text-[12px] text-ink-400">
            <Icon name="file-text" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
            Brak plików. Kliknij <strong>Dodaj pliki</strong> aby załączyć dokumenty.<br/>
            Każdy plik możesz oznaczyć indywidualnie czy ma być dostępny dla AI-draftów (RAG).
          </div>
        )}
        {files && files.length > 0 && (
          <table className="table-hw">
            <thead>
              <tr>
                <th>Nazwa</th>
                <th>Typ opr.</th>
                <th className="text-right">Rozmiar</th>
                <th>Indeks</th>
                <th>RAG (AI)</th>
                <th>Dodany</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {files.map(f => {
                const toggling = togglingId === f.id;
                const pending = f.deletionRequestedAt != null;
                const daysLeft = f.deleteAfter
                  ? Math.max(0, Math.ceil((new Date(f.deleteAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
                  : null;
                return (
                <tr key={f.id} className={(toggling ? 'opacity-50' : '') + (pending ? ' bg-red-50/40' : '')}>
                  <td>
                    {isPreviewable(f.name) ? (
                      <button type="button" onClick={() => setPreview(f)}
                        className={'font-semibold text-[13px] max-w-[320px] truncate block text-left hover:underline '
                          + (pending ? 'text-red-700 line-through' : 'text-water-700 hover:text-water-900')}
                        title={'Podgląd: ' + f.name}>{f.name}</button>
                    ) : (
                      <div className={'font-semibold text-[13px] max-w-[320px] truncate '
                        + (pending ? 'text-red-700 line-through' : 'text-ink-900')}
                        title={f.name}>{f.name}</div>
                    )}
                    {f.docCategoryName && (
                      <span className="pill pill-earth text-[10px] mt-0.5 inline-block" title="Kategoria dokumentu">{f.docCategoryName}</span>
                    )}
                    {f.sourceLabel && (
                      <span className="pill pill-water text-[10px] mt-0.5 mr-1 inline-block" title="Plik wgrany przy ofercie">{f.sourceLabel}</span>
                    )}
                    {scope !== 'borehole' && f.boreholeDesignation && (
                      <span className="pill text-[10px] mt-0.5 inline-block" title="Plik przypisany też do otworu">otwór {f.boreholeDesignation}</span>
                    )}
                    {f.contentSha256 && (
                      <div className="font-mono text-[10px] text-ink-400">
                        sha256:{f.contentSha256.slice(0, 12)}…
                      </div>
                    )}
                    {pending && (
                      <div className="text-[10px] text-red-700 font-semibold mt-0.5"
                           title={`Oznaczone przez ${f.deletionRequestedByName ?? '?'}`}>
                        ⏱ usunięcie: {daysLeft != null && daysLeft > 0
                          ? `za ${daysLeft} ${daysLeft === 1 ? 'dzień' : 'dni'}`
                          : 'dzisiaj/przeterminowane'} ({f.deleteAfter})
                      </div>
                    )}
                  </td>
                  <td>
                    {f.studyType
                      ? <span className="pill pill-earth" title={f.studyTypeName ?? ''}>
                          {f.studyTypeAbbr ?? f.studyType}
                        </span>
                      : <span className="text-ink-400 text-[11px] italic">—</span>}
                  </td>
                  <td className="text-right font-mono tabular-nums text-[12px]">
                    {f.sizeBytes != null
                      ? f.sizeBytes < 1024 ? `${f.sizeBytes} B`
                      : f.sizeBytes < 1024 * 1024 ? `${(f.sizeBytes / 1024).toFixed(1)} KB`
                      : `${(f.sizeBytes / 1024 / 1024).toFixed(1)} MB`
                      : '—'}
                  </td>
                  <td>
                    {f.indexed
                      ? <span className="pill pill-green">OK</span>
                      : <span className="pill pill-amber">brak</span>}
                  </td>
                  <td>
                    {/* Dla faktur przełącznik jest MARTWY, nie tylko domyślnie wyłączony: włączenie
                        RAG uruchomiłoby reindeks dokumentu finansowego do korpusów asystenta,
                        omijając bramkę FINANCE_VIEW. Blokada w polu staging to za mało — tu jest
                        drugi, równie klikalny przycisk. */}
                    {ragLocked ? (
                      <span className="pill pill-gray" title="Faktury nie są indeksowane dla AI (dokument finansowy).">
                        nie
                      </span>
                    ) : (
                      <button
                        onClick={() => onToggleRag(f)}
                        disabled={toggling}
                        className="text-[11px] hover:underline"
                        title={f.ragEnabled
                          ? 'Plik dostępny dla AI-draftów. Kliknij aby wyłączyć.'
                          : 'Plik POMINIĘTY przez AI. Kliknij aby włączyć indeksację.'}
                      >
                        {f.ragEnabled
                          ? <span className="pill pill-green">tak</span>
                          : <span className="pill pill-amber">nie</span>}
                      </button>
                    )}
                  </td>
                  <td className="text-[11px] text-ink-500 font-mono">
                    {f.createdAt ? new Date(f.createdAt).toLocaleDateString('pl-PL') : '—'}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {isPreviewable(f.name) && (
                      <button type="button" onClick={() => setPreview(f)}
                              className="text-water-700 hover:text-water-900 px-1"
                              title="Podgląd">
                        <Icon name="eye" className="w-3.5 h-3.5 inline"/>
                      </button>
                    )}
                    <a href={`/api/archive/documents/${f.id}/download?disposition=attachment`}
                       className="text-water-700 hover:text-water-900 px-1"
                       title="Pobierz">
                      <Icon name="download" className="w-3.5 h-3.5 inline"/>
                    </a>
                    {pending ? (
                      <button onClick={() => onCancelDelete(f)} disabled={toggling}
                              className="text-emerald-600 hover:text-emerald-800 px-1"
                              title="Cofnij usunięcie">
                        <Icon name="check" className="w-3.5 h-3.5 inline"/>
                      </button>
                    ) : (
                      <button onClick={() => onRequestDelete(f)} disabled={toggling}
                              className="text-ink-400 hover:text-amber-700 px-1"
                              title={`Usuń (${graceDays} ${graceDays === 1 ? 'dzień' : 'dni'} grace period — można cofnąć)`}>
                        <Icon name="x" className="w-3.5 h-3.5 inline"/>
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="px-4 py-2 border-t border-ink-100 text-[10px] text-ink-400 italic">
          Pliki trafiają do archiwum portalu. Kolumna „RAG (AI)" pokazuje czy plik jest dostępny dla wyszukiwania semantycznego AI;
          można to przełączać per plik. Hard-delete — w panelu Administracja → Dane referencyjne → Archiwum RAG.
        </div>
      </div>
      {preview && <FilePreviewModal file={preview} files={(files ?? []).filter(f => isPreviewable(f.name))} onClose={() => setPreview(null)}/>}
    </div>
  );
}

type PreviewFile = Pick<ArchiveDocumentListItem, 'id' | 'name'>;

/**
 * Podgląd pliku (PDF / obraz) na prawie cały ekran — serwowany inline z archiwum, bez pobierania.
 * Z `files` modal przełącza się strzałkami ← → (klawiatura i przyciski), żeby przejrzeć
 * kilka plików bez zamykania okna.
 */
export function FilePreviewModal({ file: initial, files, onClose }: {
  file: PreviewFile; files?: PreviewFile[]; onClose: () => void;
}) {
  const [file, setFile] = useState<PreviewFile>(initial);
  const list = files && files.length > 0 ? files : [file];
  const idx = Math.max(0, list.findIndex(f => f.id === file.id));
  const go = (d: number) => setFile(list[(idx + d + list.length) % list.length]);

  const ext = extOf(file.name);
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  const isPdf = ext === 'pdf';
  const url = `/api/archive/documents/${file.id}/download?disposition=inline`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (list.length > 1 && e.key === 'ArrowRight') go(1);
      else if (list.length > 1 && e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  });

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex flex-col p-3 sm:p-4" onClick={onClose}>
      <div className="flex items-center gap-3 mb-2 text-white shrink-0" onClick={e => e.stopPropagation()}>
        <Icon name="eye" className="w-4 h-4 shrink-0"/>
        <span className="font-semibold text-[13px] truncate min-w-0" title={file.name}>{file.name}</span>
        {list.length > 1 && (
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" onClick={() => go(-1)}
                    className="px-2 py-1 rounded text-[12px] bg-white/15 hover:bg-white/25" title="Poprzedni (←)">←</button>
            <span className="text-[12px] font-mono tabular-nums text-white/80">{idx + 1} / {list.length}</span>
            <button type="button" onClick={() => go(1)}
                    className="px-2 py-1 rounded text-[12px] bg-white/15 hover:bg-white/25" title="Następny (→)">→</button>
          </div>
        )}
        {/* shrink-0: bez tego długa nazwa pliku wypycha akcje (w tym „✕") poza ekran modala */}
        <div className="ml-auto flex items-center gap-1 shrink-0">
          <a href={url} target="_blank" rel="noreferrer"
             className="px-2 py-1 rounded text-[12px] bg-white/15 hover:bg-white/25" title="Otwórz w nowej karcie">Nowa karta</a>
          <a href={`/api/archive/documents/${file.id}/download?disposition=attachment`}
             className="px-2 py-1 rounded text-[12px] bg-white/15 hover:bg-white/25" title="Pobierz">Pobierz</a>
          <button type="button" onClick={onClose}
                  className="px-2 py-1 rounded text-[12px] bg-white/15 hover:bg-white/25" title="Zamknij (Esc)">✕</button>
        </div>
      </div>
      <div className="flex-1 min-h-0 bg-white rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        {isPdf ? (
          <iframe src={url} title={file.name} className="w-full h-full border-0"/>
        ) : isImage ? (
          <div className="w-full h-full overflow-auto grid place-items-center bg-ink-900">
            <img src={url} alt={file.name} className="max-w-full max-h-full object-contain"/>
          </div>
        ) : (
          <div className="p-8 text-center text-ink-500 text-[13px]">
            Podgląd niedostępny dla tego typu pliku.{' '}
            <a href={url} target="_blank" rel="noreferrer" className="text-water-600 underline">Otwórz w nowej karcie</a>
          </div>
        )}
      </div>
    </div>
  );
}
